import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Dialog from "../../../../../../../common/element/Dialog";
import { 
  getCandidateAssessmentLink,
  clearAssessmentLinkState 
} from "../../../../../../../redux/InterViewCandidate/interViewCandidateSlice";
import {
  selectAssessmentLinkLoading,
  selectAssessmentLinkSuccess,
  selectAssessmentLinkError,
  selectAssessmentLinkData
} from "../../../../../../../redux/InterViewCandidate/interViewSelector";
import ThreeDotBouncingLoader from "../../../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";

const InterviewURLModal = ({ dialogOpen, onDialogClose, candidateData }) => {
  const dispatch = useDispatch();
  console.log(candidateData?.candidate_id ,"candidateData.candidate_idcandidateData.candidate_id")
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("companyId");
  
  const [copied, setCopied] = useState(false);

  // Redux selectors
  const loading = useSelector(selectAssessmentLinkLoading);
  const success = useSelector(selectAssessmentLinkSuccess);
  const error = useSelector(selectAssessmentLinkError);
  const assessmentData = useSelector(selectAssessmentLinkData);

  // Fetch assessment link when modal opens
  useEffect(() => {
    if (dialogOpen && candidateData ) {
      dispatch(getCandidateAssessmentLink({
        company_id: Number(companyId),
        candidate_id: candidateData.candidate_id || candidateData.id
      }));
      setCopied(false);
    }
  }, [dialogOpen, candidateData, companyId, dispatch]);

  // Clear state when modal closes
  useEffect(() => {
    if (!dialogOpen) {
      dispatch(clearAssessmentLinkState());
    }
  }, [dialogOpen, dispatch]);

  // Get the URL from the API response - handle different response structures
  const interviewURL = assessmentData?.data?.assessment_link || 
                       assessmentData?.data?.interview_url ||
                       assessmentData?.data?.url ||
                       assessmentData?.data?.link ||
                       assessmentData?.assessment_link || 
                       assessmentData?.interview_url ||
                       assessmentData?.url || 
                       assessmentData?.link ||
                       "";

  const handleCopy = async () => {
    if (interviewURL) {
      try {
        await navigator.clipboard.writeText(interviewURL);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = interviewURL;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleClose = () => {
    setCopied(false);
    dispatch(clearAssessmentLinkState());
    onDialogClose();
  };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={handleClose}
      width={800}
      bodyOpenClassName="overflow-hidden"
    >
      <div className="py-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11 5m3 6a5 5 0 0 0-7.07 0l-2.83 2.83a5 5 0 0 0 7.07 7.07L13 19"
                  stroke="#344054"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <h5 className="text-gray-900 text-base font-semibold mb-0">
              Interview URL
            </h5>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <ThreeDotBouncingLoader />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 20 20" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" 
                    stroke="#DC2626" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-red-600 text-sm font-medium">
                  {typeof error === 'string' ? error : "Failed to fetch interview URL"}
                </span>
              </div>
            </div>
          ) : success && interviewURL ? (
            <>
              <div className="flex items-center gap-3">
                {/* Label */}
                <label className="text-sm font-medium text-gray-700 w-[120px] flex-shrink-0">
                  Interview URL
                </label>

                {/* Input */}
                <input
                  type="text"
                  value={interviewURL}
                  readOnly
                  className="h-10 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-50"
                />

                {/* Copy Button */}
                <button
                  onClick={handleCopy}
                  disabled={!interviewURL}
                  className="h-[38px] w-[38px] flex-shrink-0 flex items-center justify-center bg-[#FF8E1C] rounded-lg hover:bg-[#e57d12] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title="Copy URL"
                >
                  {copied ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.083 2.57C15.7094 2.20466 15.2076 2.00007 14.685 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 18V20C16 20.5304 15.7893 21.0391 15.4142 21.4142C15.0391 21.7893 14.5304 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H8"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* Copy feedback */}
              {copied && (
                <div className="flex items-center gap-2 mt-3 ml-[132px]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="#16A34A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-sm text-green-600 font-medium">
                    Copied to clipboard!
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="mx-auto mb-3"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11 5m3 6a5 5 0 0 0-7.07 0l-2.83 2.83a5 5 0 0 0 7.07 7.07L13 19"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-sm">No interview URL available</p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default InterviewURLModal;